import type { InputSource, PhaseTiming, ResponseCode } from '@/domain/core/types';
import type { SessionScores } from '@/domain/scoring/types';
import type { FrameQuality } from '@/engine/Clock';

export const SCHEMA_VERSION = 3;

export interface ParticipantRecord {
  id: string;            // 내부 uuid
  code: string;          // 참가자 코드 (PII 아님)
  createdAt: string;
  note?: string;
}

export interface DeviceStamp {
  platform: string;
  userAgent: string;
  model: string | null;
  osVersion: string | null;
  devicePixelRatio: number;
  screenCssPx: { w: number; h: number };
  refreshHz: number;
  frameMs: number;
  frameSdMs: number;
  timerResolutionMs: number | null;
  crossOriginIsolated: boolean;
  desynchronized: boolean;
  inputModeHint: 'keyboard' | 'pointer' | 'mixed' | 'unknown';
  pxPerMm: number | null;
  viewingDistanceMm: number | null;
  /** 물리 캘리브레이션으로 배치했는가. false 면 논리 단위 — 시각도 비교 불가. */
  physicalLayout: boolean;
}

export type SessionArm = 'practice' | 'assessment' | 'training' | 'sham';

export interface SessionRecord {
  programContext?: { id: string; day: number; version: string };
  /** Added in v0.2; absent in historical dark-theme sessions. */
  presentation?: {
    theme: 'light' | 'dark';
    background: string;
    foreground: string;
    correctnessFeedback: 'practice-only';
    fontFamily: string;
    displayMode?: 'window' | 'immersive';
  };
  id: string;
  participantId: string;
  participantCode: string;
  arm: SessionArm;
  /** 임상 통합 모델: 누가 이 세션을 처방·감독했는가. 재택 자가시행이면 'self'. */
  supervision: 'clinician' | 'self';
  protocolId: string;
  protocolVersion: string;
  protocolHash: string;
  stimulusSetId: string;
  stimulusSetVersion: string;
  stimulusSetStatus: string;
  /** ★ 이것만으로 트라이얼 순서를 완전 재현한다 */
  rngSeed: number;
  appVersion: string;
  schemaVersion: number;
  signConvention: 'vigilance-positive';
  device: DeviceStamp;
  startedAt: string;
  endedAt: string | null;
  abortedReason: string | null;
  trialCount: number;
  quality: FrameQuality | null;
}

export interface TrialRecord {
  pk?: number;
  sessionId: string;
  trialIndex: number;
  blockId: string;
  // 조건 (원값 보존 — 차이점수는 저장하지 않는다)
  arm: SessionArm;
  trialType: 'threat-neutral' | 'neutral-neutral';
  pairId: string;
  threatSide: string | null;
  probeSide: string;
  congruency: 'congruent' | 'incongruent' | 'neutral';
  soaMs: number;
  // 반응
  onsetTs: number | null;
  responseTs: number | null;
  rt: number | null;
  response: ResponseCode | null;
  correct: boolean | null;
  timedOut: boolean;
  aborted: boolean;
  inputSource: InputSource | null;
  handlerDelayMs: number | null;
  prematureInputs: number;
  // 타이밍 감사
  phases: PhaseTiming[];
}

export interface EventRecord {
  pk?: number;
  sessionId: string;
  ts: number;
  at: string;
  topic: string;
  payload?: unknown;
}

export interface ScoreRecord {
  previousScoring?: { scoringVersion: string; computedAt: string; scores: unknown };
  id: string;
  sessionId: string;
  participantId: string;
  arm: SessionArm;
  computedAt: string;
  scoringVersion: string;
  scores: SessionScores;
}

export interface ProtocolRecord {
  id: string;                 // uuid
  /** `${protocolId}@${version}` — 유일 */
  key: string;
  protocolId: string;
  version: string;
  name: string;
  task: 'CBM_A';
  raw: unknown;               // 스키마를 통과한 원본 JSON
  hash: string;               // SHA-256
  source: 'builtin' | 'imported';
  createdAt: string;
}
